// components/activities/ConversationSimulation.jsx - FIXED: Theory only shows once
import React, { useState, useEffect } from 'react';
import { Button, Card, TextArea, Modal } from '../ui/UIComponents';
import { conversationsAPI, lessonsAPI } from '../../services/api';

// PersonaAvatar Component - Simple placeholder that can be replaced with URL later
const PersonaAvatar = ({ persona, size = 'lg' }) => {
  const { name, avatarUrl } = persona;
  
  const sizeClasses = {
    sm: 'w-12 h-12 text-lg',
    md: 'w-16 h-16 text-2xl', 
    lg: 'w-24 h-24 text-4xl',
    xl: 'w-32 h-32 text-5xl'
  };
  
  // Future: when avatarUrl is available
  if (avatarUrl) {
    return (
      <img 
        src={avatarUrl} 
        alt={name}
        className={`${sizeClasses[size]} rounded-full object-cover shadow-lg`}
      />
    );
  }
  
  // Current: simple placeholder with initial
  const initial = name ? name.charAt(0).toUpperCase() : '?';
  
  return (
    <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold shadow-lg`}>
      {initial}
    </div>
  );
};

// Compact Instruction Box - Always visible above chat
const CompactInstructionBox = ({ passingCriteria, conversationLength }) => {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        <div className="text-blue-600 text-xl flex-shrink-0">📋</div>
        <div className="flex-1">
          <div className="text-blue-900 font-semibold text-sm mb-1">Jouw opdracht:</div>
          <p className="text-blue-800 text-sm leading-relaxed">
            {passingCriteria}
          </p>
        </div>
        {conversationLength >= 3 && (
          <div className="flex-shrink-0">
            <div className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
              {conversationLength} berichten
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ConversationSimulation = ({ 
  moduleConfig, 
  onComplete, 
  onProgress 
}) => {
  const [currentConversation, setCurrentConversation] = useState(1);
  const [conversationId, setConversationId] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [userMessage, setUserMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [error, setError] = useState(null);
  const [canEvaluate, setCanEvaluate] = useState(false);
  const [hasEvaluationFeedback, setHasEvaluationFeedback] = useState(false);
  
  // FIXED: Track if theory has been completed to prevent showing it again
  const [theoryCompleted, setTheoryCompleted] = useState(false);
  const [currentScreen, setCurrentScreen] = useState('theory');
  
  // ENHANCED: New state for evaluation progress
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [evaluationProgress, setEvaluationProgress] = useState({});
  const [evaluationError, setEvaluationError] = useState(null);
  
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [personaLoading, setPersonaLoading] = useState(false);

  // NEW: Theory section navigation state
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [completedSections, setCompletedSections] = useState(new Set());

  const totalConversations = moduleConfig.conversations.total;

  // FIXED: Only reset conversation-specific state, NOT theory state or screen
  useEffect(() => {
    // Reset conversation-specific state only
    setConversationHistory([]);
    setUserMessage('');
    setEvaluation(null);
    setShowEvaluation(false);
    setConversationId(null);
    setCanEvaluate(false);
    setSelectedPersona(null);
    setError(null);
    setHasEvaluationFeedback(false);
    setIsEvaluating(false);
    setEvaluationProgress({});
    setEvaluationError(null);
    
    // FIXED: Don't reset to theory if theory is already completed
    // This prevents showing theory again when moving from conversation 1 to 2
    if (!theoryCompleted) {
      setCurrentScreen('theory');
      setCurrentSectionIndex(0);
      setCompletedSections(new Set());
    }
    // If theory is already done, the conversation will start automatically via startConversation call
  }, [currentConversation, theoryCompleted]);

  // UPDATED: Changed threshold from >= 3 to >= 4
  useEffect(() => {
    setCanEvaluate(conversationHistory.length >= 4);
  }, [conversationHistory]);

  // NEW: Auto-scroll effect for conversation screen
  useEffect(() => {
    if (currentScreen !== 'conversation') return;

    if (isEvaluating) {
      // Show evaluation progress at top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else if (conversationHistory.length > 0) {
      // Show new messages at bottom
      requestAnimationFrame(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth'
        });
      });
    }
  }, [conversationHistory.length, isEvaluating, currentScreen]);

  // On mount: detect if the user already completed a conversation in a previous
  // session and skip directly to the next one (Level A refresh resume)
  useEffect(() => {
    const resumeFromProgress = async () => {
      try {
        const data = await lessonsAPI.getProgress();
        if (!data?.progress?.conversations) return;

        const passed = data.progress.conversations.filter(
          c => c.moduleId === moduleConfig.id && c.evaluation?.passed === true
        );
        if (passed.length === 0 || passed.length >= totalConversations) return;

        const highestPassed = Math.max(...passed.map(c => c.conversationNumber));
        const nextConversation = highestPassed + 1;

        // Batch both updates so the currentConversation effect sees
        // theoryCompleted=true and doesn't flash back to the theory screen
        setTheoryCompleted(true);
        setCurrentConversation(nextConversation);
        setTimeout(() => startConversation(nextConversation), 100);
      } catch (err) {
        // Non-critical — fall back to starting from conversation 1
        console.warn('Could not resume conversation from progress:', err.message);
      }
    };

    resumeFromProgress();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // NEW: Theory section handlers
  const handleSectionComplete = () => {
    const currentSection = moduleConfig.theory.sections[currentSectionIndex];
    const newCompleted = new Set(completedSections);
    newCompleted.add(currentSection.id);
    setCompletedSections(newCompleted);

    if (onProgress) {
      onProgress({
        sectionsCompleted: newCompleted.size,
        totalSections: moduleConfig.theory.sections.length,
        currentSection: currentSection.id
      });
    }

    const isLastSection = currentSectionIndex === moduleConfig.theory.sections.length - 1;
    if (!isLastSection) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    }
  };

  const handleSectionJump = (index) => {
    setCurrentSectionIndex(index);
  };

  const startConversation = async (conversationNum = null) => {
    try {
      setIsLoading(true);
      setPersonaLoading(true);
      setError(null);

      // FIXED: Mark theory as completed when starting first conversation
      if (!theoryCompleted) {
        setTheoryCompleted(true);
      }

      // Use provided conversation number or fall back to state
      const convNum = conversationNum !== null ? conversationNum : currentConversation;

      const data = await conversationsAPI.start(moduleConfig.id, convNum);

      if (!data?.success || !data?.conversationId || !data?.persona) {
        throw new Error('Invalid response from server');
      }

      const requiredFields = ['id', 'name', 'background'];
      const missingFields = requiredFields.filter(field => !data.persona[field]);
      if (missingFields.length > 0) {
        throw new Error(`Persona missing required fields: ${missingFields.join(', ')}`);
      }

      setConversationId(data.conversationId);
      setSelectedPersona(data.persona);
      setCurrentScreen('persona');
      
    } catch (err) {
      const errorMessage = err.message || 'Unknown error occurred';
      setError(`Kon gesprek niet starten: ${errorMessage}`);
      setCurrentScreen('theory');
    } finally {
      setIsLoading(false);
      setPersonaLoading(false);
    }
  };

  const beginConversation = () => {
    setCurrentScreen('conversation');
  };

  const sendMessage = async () => {
    if (!userMessage.trim() || !conversationId || !selectedPersona) return;

    try {
      setIsLoading(true);
      setError(null);

      const data = await conversationsAPI.sendMessage(
        conversationId,
        userMessage.trim()
      );
      
      const newExchange = {
        userMessage: userMessage.trim(),
        clientResponse: data.clientResponse,
        timestamp: new Date()
      };

      setConversationHistory(prev => [...prev, newExchange]);
      setUserMessage('');

      if (onProgress) {
        onProgress({
          conversation: currentConversation,
          exchangeCount: conversationHistory.length + 1
        });
      }

    } catch (err) {
      console.error('Message send error:', err);
      if (err.message?.includes('AI_OVERLOADED')) {
        setError('Het AI-systeem is op dit moment niet beschikbaar. Probeer het over een paar seconden opnieuw. Lukt het dan nog niet? Probeer het dan op een later moment — er kan een tijdelijke storing zijn.');
      } else {
        setError('Kon bericht niet versturen. Probeer het opnieuw.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const evaluateConversation = async () => {
    if (!conversationId || !selectedPersona) return;

    try {
      setIsEvaluating(true);
      setEvaluationError(null);
      setError(null);
      setEvaluationProgress({});

      const handleProgress = (progressData) => {
        setEvaluationProgress(progressData);
      };

      const data = await conversationsAPI.evaluate(
        conversationId, 
        conversationHistory, 
        moduleConfig.id, 
        selectedPersona,
        handleProgress
      );
      
      if (data.success && data.evaluation) {
        setEvaluation(data.evaluation);
        setShowEvaluation(true);
        
        if (data.recovered) {
          setEvaluationProgress({
            stage: 'recovered',
            message: 'Evaluatie succesvol hersteld uit database!'
          });
        }
      } else {
        throw new Error('Invalid evaluation response');
      }

    } catch (err) {
      console.error('Evaluation error:', err);
      
      const errorMessage = err.message || 'Unknown evaluation error';
      setEvaluationError(errorMessage);
      
      if (errorMessage.includes('AI_OVERLOADED')) {
        setError('Het AI-systeem is op dit moment niet beschikbaar. Probeer het over een paar seconden opnieuw. Lukt het dan nog niet? Probeer het dan op een later moment — er kan een tijdelijke storing zijn.');
      } else if (errorMessage.includes('timeout')) {
        setError('Evaluatie duurde te lang. De evaluatie wordt mogelijk nog verwerkt op de achtergrond. Probeer over een minuut opnieuw te controleren.');
      } else if (errorMessage.includes('gefaald na')) {
        setError('Evaluatie gefaald na meerdere pogingen. Controleer je internetverbinding en probeer het opnieuw.');
      } else {
        setError(`Kon gesprek niet evalueren: ${errorMessage}`);
      }
    } finally {
      setIsEvaluating(false);
      setEvaluationProgress({});
    }
  };

  const handleEvaluationContinue = async () => {
    if (evaluation.passed) {
      if (currentConversation < totalConversations) {
        const nextConversation = currentConversation + 1;
        setShowEvaluation(false);
        setCurrentConversation(nextConversation);
        // FIXED: Pass the next conversation number explicitly to avoid async state issues
        // This ensures the correct persona is loaded (e.g., Sanne then Yara)
        setTimeout(() => {
          startConversation(nextConversation);
        }, 100);
      } else {
        onComplete({
          completed: true,
          totalConversations,
          allPassed: true
        });
      }
    } else {
      setShowEvaluation(false);
      setEvaluation(null);
      setHasEvaluationFeedback(true);
    }
  };

  const handleStartOver = () => {
    setShowEvaluation(false);
    setConversationHistory([]);
    setUserMessage('');
    setConversationId(null);
    setCanEvaluate(false);
    setSelectedPersona(null);
    setEvaluation(null);
    setHasEvaluationFeedback(false);
    setIsEvaluating(false);
    setEvaluationProgress({});
    setEvaluationError(null);
    // Start over means restarting the current conversation with a new persona
    startConversation();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // NEW: Open theory in new tab using Blob URL
  const openTheoryInNewTab = () => {
    if (!moduleConfig.theory) return;
    
    const formatTheoryHTML = (content) => {
      return content.split('\n').map(line => {
        const trimmed = line.trim();
        if (!trimmed) return '<br>';
        
        if (trimmed.includes('**')) {
          const formatted = trimmed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          return `<p style="margin-bottom: 15px;">${formatted}</p>`;
        }
        
        return `<p style="margin-bottom: 15px;">${trimmed}</p>`;
      }).join('');
    };
    
    const formatTheorySections = (sections) => {
      return sections.map(section => `
        <div style="margin-bottom: 40px;">
          <h2 style="color: #6366f1; margin-bottom: 20px; font-size: 1.5em;">${section.title}</h2>
          ${formatTheoryHTML(section.content)}
        </div>
      `).join('');
    };
    
    const theoryHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Theorie - ${moduleConfig.title}</title>
        <style>
          body {
            font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            max-width: 800px;
            margin: 40px auto;
            padding: 20px;
            line-height: 1.6;
            color: #1f2937;
            background: #f9fafb;
          }
          h1 { 
            color: #7c3aed; 
            margin-bottom: 30px; 
            font-size: 2em;
            font-weight: 600;
          }
          h2 {
            color: #6366f1;
            margin-bottom: 20px;
            font-size: 1.5em;
          }
          p { 
            margin-bottom: 15px; 
            color: #374151;
          }
          strong { 
            color: #4f46e5; 
            font-weight: 600;
          }
        </style>
      </head>
      <body>
        <h1>${moduleConfig.theory.title || 'Theorie'}</h1>
        ${moduleConfig.theory.sections ? 
          formatTheorySections(moduleConfig.theory.sections) : 
          formatTheoryHTML(moduleConfig.theory.content || '')
        }
      </body>
      </html>
    `;
    
    const blob = new Blob([theoryHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const formatContent = (content) => {
    const lines = content.split('\n');
    const result = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      
      if (!trimmed) {
        result.push(null);
        continue;
      }
      
      // Check if this line is a title followed by a URL on the next line
      const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : '';
      const urlRegex = /^https?:\/\/[^\s]+$/;
      
      if (nextLine && urlRegex.test(nextLine)) {
        // This is a title with URL - format as clickable title
        result.push(
          <div key={i} className="mb-3">
            <a 
              href={nextLine}
              target="_blank" 
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-800 underline font-medium text-lg"
            >
              {trimmed}
            </a>
          </div>
        );
        i++; // Skip the URL line since we've processed it
        continue;
      }
      
      // Handle headers starting with **
      if (trimmed.includes('**') && trimmed.startsWith('**')) {
        const headerText = trimmed.replace(/\*\*/g, '');
        result.push(
          <h3 key={i} className="font-header text-xl font-medium text-charcoal-800 mt-6 mb-3">
            {headerText}
          </h3>
        );
        continue;
      }
      
      // Handle bold text within paragraphs
      if (trimmed.includes('**')) {
        const parts = trimmed.split('**');
        result.push(
          <p key={i} className="text-charcoal-700 mb-4 leading-relaxed font-body">
            {parts.map((part, partIndex) => 
              partIndex % 2 === 1 ? (
                <strong key={partIndex} className="font-semibold text-charcoal-900">
                  {part}
                </strong>
              ) : (
                part
              )
            )}
          </p>
        );
        continue;
      }
      
      // Handle bullet points
      if (trimmed.startsWith('-')) {
        result.push(
          <li key={i} className="text-charcoal-700 mb-2 ml-4 font-body">
            {trimmed.replace(/^\-\s*/, '')}
          </li>
        );
        continue;
      }
      
      // Check for quotes and format them as italic
      if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
        result.push(
          <p key={i} className="text-charcoal-700 mb-2 leading-relaxed font-body italic">
            {trimmed}
          </p>
        );
        continue;
      }
      
      // Check for URLs and make them clickable
      const urlRegexInline = /(https?:\/\/[^\s]+)/g;
      if (urlRegexInline.test(trimmed)) {
        const parts = trimmed.split(urlRegexInline);
        result.push(
          <p key={i} className="text-charcoal-700 mb-4 leading-relaxed font-body">
            {parts.map((part, partIndex) => {
              if (urlRegexInline.test(part)) {
                return (
                  <a 
                    key={partIndex}
                    href={part} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-800 underline font-medium"
                  >
                    {part}
                  </a>
                );
              }
              return part;
            })}
          </p>
        );
        continue;
      }
      
      // Regular paragraphs
      result.push(
        <p key={i} className="text-charcoal-700 mb-4 leading-relaxed font-body">
          {trimmed}
        </p>
      );
    }
    
    return result;
  };

  // SCREEN 1: Theory with Section Navigation
  if (currentScreen === 'theory') {
    // Safety check for theory sections
    if (!moduleConfig?.theory?.sections || moduleConfig.theory.sections.length === 0) {
      return (
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center">
            <p className="text-charcoal-600">Geen theorie secties beschikbaar.</p>
            <Button onClick={() => setCurrentScreen('persona')}>
              Doorgaan naar gesprek
            </Button>
          </div>
        </div>
      );
    }

    const totalSections = moduleConfig.theory.sections.length;
    const currentSection = moduleConfig.theory.sections[currentSectionIndex];
    const isLastSection = currentSectionIndex === totalSections - 1;
    const allSectionsCompleted = completedSections.size === totalSections;

    return (
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-title text-4xl font-light text-purple-600 mb-2">
            {moduleConfig.title}
          </h1>
          <div className="flex items-center justify-center space-x-4 text-sm text-charcoal-600 font-body">
            <span>⏱️ {moduleConfig.duration} minuten</span>
            <span>Gesprek {currentConversation} van {totalConversations}</span>
            <span>
              {completedSections.size}/{totalSections} secties voltooid
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-2">
            {moduleConfig.theory.sections.map((section, index) => (
              <button
                key={section.id}
                onClick={() => handleSectionJump(index)}
                className={`flex-1 h-2 rounded-full transition-colors duration-200 ${
                  index === currentSectionIndex
                    ? 'bg-purple-600'
                    : completedSections.has(section.id)
                      ? 'bg-green-500'
                      : 'bg-charcoal-300'
                }`}
                title={section.title}
              />
            ))}
          </div>
          <div className="flex justify-between text-xs text-charcoal-500 font-body">
            <span>Sectie {currentSectionIndex + 1}</span>
            <span>{totalSections} secties totaal</span>
          </div>
        </div>

        {/* Section Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {moduleConfig.theory.sections.map((section, index) => (
            <button
              key={section.id}
              onClick={() => handleSectionJump(index)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 font-body ${
                index === currentSectionIndex
                  ? 'bg-purple-600 text-white'
                  : completedSections.has(section.id)
                    ? 'bg-green-100 text-green-800'
                    : 'bg-cream-200 text-charcoal-600 hover:bg-cream-300'
              }`}
            >
              {completedSections.has(section.id) && '✓ '}
              {section.title}
            </button>
          ))}
        </div>

        {/* Current Section Content */}
        <div className="bg-white rounded-container shadow-soft border border-cream-200 p-8 mb-8">
          <h2 className="font-header text-2xl font-medium text-charcoal-800 mb-6">
            {currentSection.title}
          </h2>
          
          <div className="prose prose-lg max-w-none">
            {formatContent(currentSection.content)}
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => handleSectionJump(Math.max(0, currentSectionIndex - 1))}
            disabled={currentSectionIndex === 0}
          >
            ← Vorige Sectie
          </Button>

          <div className="text-center">
            <p className="text-sm text-charcoal-600 mb-2 font-body">
              Sectie {currentSectionIndex + 1} van {totalSections}
            </p>
            {!completedSections.has(currentSection.id) && (
              <p className="text-xs text-charcoal-500 font-body">
                Klik "Sectie Voltooid" om door te gaan
              </p>
            )}
          </div>

          {!completedSections.has(currentSection.id) ? (
            <Button
              variant="primary"
              onClick={handleSectionComplete}
            >
              {isLastSection ? 'Theorie Voltooid' : 'Sectie Voltooid'}
            </Button>
          ) : !isLastSection ? (
            <Button
              variant="primary"
              onClick={() => setCurrentSectionIndex(currentSectionIndex + 1)}
            >
              Volgende Sectie →
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => startConversation()}
              disabled={isLoading || personaLoading}
              size="lg"
            >
              {isLoading ? 'Voorbereiden...' : 'Start Gesprek'}
            </Button>
          )}
        </div>

        {/* Show error if persona loading failed */}
        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="text-red-500 text-xl mr-3">⚠️</div>
              <div className="flex-1">
                <h4 className="font-medium text-red-800 mb-2">Er is een fout opgetreden</h4>
                <p className="text-red-700 text-sm mb-3">{error}</p>
                <div className="flex space-x-2">
                  <Button 
                    size="sm" 
                    onClick={() => startConversation()}
                    disabled={isLoading}
                  >
                    Probeer Opnieuw
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setError(null)}
                  >
                    Sluiten
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading indicator for persona */}
        {personaLoading && (
          <div className="mt-6 bg-white rounded-container shadow-soft border border-cream-200 p-8 text-center">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="font-header text-xl font-medium text-charcoal-800 mb-2">Gesprek voorbereiden...</h2>
            <p className="text-charcoal-600 font-body">We selecteren een geschikte gesprekspartner voor jouw vakgebied.</p>
          </div>
        )}
      </div>
    );
  }

  // SCREEN 2: Persona introduction
  if (currentScreen === 'persona' && selectedPersona) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Gesprek {currentConversation} van {totalConversations}
          </h2>
        </div>

        <Card className="bg-gradient-to-br from-blue-50 to-purple-50">
          <div className="flex justify-center mb-6">
            <PersonaAvatar persona={selectedPersona} size="xl" />
          </div>
          
          <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
            {selectedPersona.name}
          </h3>
          
          <div className="space-y-4">
            <div>
              <span className="font-semibold text-gray-700">Achtergrond:</span>
              <p className="text-gray-600 mt-2 leading-relaxed">
                {selectedPersona.backgroundUser}
              </p>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <Button 
              onClick={beginConversation}
              disabled={isLoading}
              size="lg"
            >
              Begin het Gesprek
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // SCREEN 3: Conversation screen
  if (currentScreen === 'conversation' && selectedPersona) {
    return (
      <div className="max-w-5xl mx-auto px-4">
        {/* Simplified Header */}
        <div className="flex items-center justify-between mb-6 mt-6">
          <div className="flex items-center gap-3">
            <PersonaAvatar persona={selectedPersona} size="sm" />
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                Gesprek met {selectedPersona.name}
              </h2>
              <p className="text-sm text-gray-600">
                Gesprek {currentConversation} van {totalConversations}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={openTheoryInNewTab}
              className="text-gray-600"
            >
              📚 Theorie
            </Button>
          </div>
        </div>

        {/* Evaluation Progress */}
        {isEvaluating && (
          <Card className="mb-4">
            <div className="p-4">
              <div className="flex items-center justify-center mb-3">
                <div className="w-6 h-6 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mr-3"></div>
                <span className="text-blue-700 font-medium">
                  {evaluationProgress.message || 'Evaluatie wordt uitgevoerd...'}
                </span>
              </div>
              
              {evaluationProgress.attempt && (
                <div className="text-center">
                  <span className="text-sm text-gray-600">
                    Poging {evaluationProgress.attempt}
                  </span>
                  {evaluationProgress.error && (
                    <div className="text-xs text-orange-600 mt-1">
                      Vorige fout: {evaluationProgress.error}
                    </div>
                  )}
                </div>
              )}
              
              <div className="mt-3 text-center text-sm text-gray-500">
                <p>⏱️ Dit kan 1-2 minuten duren door de uitgebreide AI-analyse</p>
              </div>
            </div>
          </Card>
        )}

        {/* Error Display */}
        {evaluationError && !isEvaluating && (
          <Card className="mb-4 bg-orange-50 border-orange-200">
            <div className="p-4">
              <div className="flex items-start">
                <div className="text-orange-500 text-xl mr-3">⚠️</div>
                <div className="flex-1">
                  <h4 className="font-medium text-orange-800 mb-2">Evaluatie Probleem</h4>
                  <p className="text-orange-700 text-sm mb-3">{error}</p>
                  <div className="flex space-x-2">
                    <Button 
                      size="sm" 
                      onClick={evaluateConversation}
                      disabled={isEvaluating}
                    >
                      Probeer Opnieuw
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setEvaluationError(null);
                        setError(null);
                      }}
                    >
                      Sluiten
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* ALWAYS VISIBLE Compact Instruction Box */}
        <CompactInstructionBox 
          passingCriteria={moduleConfig.conversations.instruction}
          conversationLength={conversationHistory.length}
        />

        {/* Chat Messages */}
        <Card padding={false} className="mb-4">
          <div className="p-6 space-y-4">
            {conversationHistory.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <div className="text-5xl mb-4">⬇️</div>
                <p className="text-gray-600">
                  Start het gesprek door hieronder je eerste bericht te typen.
                </p>
              </div>
            )}
            
            {conversationHistory.map((exchange, index) => (
              <div key={index} className="space-y-4">
                {/* User message */}
                <div className="flex justify-end">
                  <div className="bg-green-600 text-white rounded-2xl px-6 py-4 max-w-[80%] shadow-sm">
                    <div className="text-xs opacity-90 mb-1">Jij (hulpverlener)</div>
                    <div className="leading-relaxed">{exchange.userMessage}</div>
                  </div>
                </div>
                
                {/* Client response */}
                <div className="flex justify-start">
                  <div className="bg-gray-100 text-gray-800 rounded-2xl px-6 py-4 max-w-[80%] shadow-sm">
                    <div className="text-xs text-gray-600 mb-1">{selectedPersona.name}</div>
                    <div className="leading-relaxed">{exchange.clientResponse}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* ALWAYS VISIBLE Message Input */}
        <Card>
          <div className="space-y-4">
            <TextArea
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Typ je bericht aan ${selectedPersona.name}...`}
              rows={3}
              disabled={isLoading}
              className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            />
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {hasEvaluationFeedback && (
                  <Button
                    onClick={handleStartOver}
                    variant="outline"
                    size="sm"
                  >
                    Start Opnieuw
                  </Button>
                )}
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  onClick={evaluateConversation}
                  disabled={!canEvaluate || isEvaluating}
                  variant="success"
                  size="lg"
                  title={!canEvaluate ? "Je hebt minimaal 4 interacties nodig om te evalueren" : "Evalueer je gesprek"}
                >
                  {isEvaluating ? (
                    <span className="flex items-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Evalueren...
                    </span>
                  ) : 'Evalueer Gesprek'}
                </Button>
                
                <Button
                  onClick={sendMessage}
                  disabled={!userMessage.trim() || isLoading}
                  size="lg"
                  className="px-8"
                >
                  {isLoading ? 'Versturen...' : 'Verstuur'}
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Evaluation Modal */}
        {showEvaluation && evaluation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-5xl w-full max-h-[95vh] overflow-y-auto overscroll-contain">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">
                Uitgebreide Feedback - Gesprek {currentConversation}
              </h3>
              
                <div className="space-y-6">
                {/* After-conversation messaging from module config */}
                {evaluation.passed && moduleConfig.conversations && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="text-green-600 text-2xl flex-shrink-0">🎉</div>
                      <div className="flex-1">
                        <h4 className="text-green-800 font-semibold mb-1">
                          {currentConversation === 1 ? 'Casus 1 voltooid' : 'Casus voltooid'}
                        </h4>
                        <p className="text-green-700 text-sm leading-relaxed whitespace-pre-line">
                          {currentConversation === 1 
                            ? (moduleConfig.conversations.afterConversation1 || '')
                            : (moduleConfig.conversations.afterConversation2 || '')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {/* Task completion status */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center justify-center mb-3">
                    <div className={`text-3xl mr-3 ${
                      evaluation.passed ? 'text-green-600' : 'text-orange-600'
                    }`}>
                      {evaluation.passed ? '✅' : '💡'}
                    </div>
                    <div>
                      <div className={`text-lg font-medium ${
                        evaluation.passed ? 'text-green-700' : 'text-orange-700'
                      }`}>
                        {evaluation.passed ? 'Opdracht voltooid!' : 'Nog even oefenen'}
                      </div>
                      <p className="text-gray-600 text-sm">
                        {evaluation.passed ? 
                          'Je hebt de leerdoelen behaald.' :
                          'Ga verder met oefenen om de leerdoelen te behalen.'
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Module 6 Specific Progress Tracking */}
                {moduleConfig.id === 'module6' && evaluation.clientChangeTalkUtterances !== undefined && (
                  <div className="bg-purple-50 border-2 border-purple-200 p-5 rounded-lg">
                    <h4 className="font-semibold text-purple-800 mb-4 flex items-center text-lg">
                      📊 Voortgang Eindopdracht
                    </h4>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Verandertaal-uitspraken cliënt (minimaal 6 vereist):
                        </span>
                        <span className={`text-lg font-bold ${
                          (evaluation.clientChangeTalkUtterances || 0) >= 6
                            ? 'text-green-600'
                            : 'text-orange-600'
                        }`}>
                          {evaluation.clientChangeTalkUtterances || 0}/6
                        </span>
                      </div>
                      {!evaluation.passed && (evaluation.clientChangeTalkUtterances || 0) < 6 && (
                        <div className="mt-2 text-sm text-orange-700 bg-orange-50 p-2 rounded">
                          <strong>Nog te doen:</strong> Lok nog {6 - (evaluation.clientChangeTalkUtterances || 0)} verandertaal-uitspraak(en) uit bij de cliënt
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Comprehensive feedback sections */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="bg-blue-50 p-5 rounded-lg">
                      <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                        Voortgang Opdracht
                      </h4>
                      <div className="text-blue-700 break-words">
                        {evaluation.feedback 
                          ? (
                            <div className="prose prose-sm max-w-none break-words">
                              {formatContent(evaluation.feedback)}
                            </div>
                          ) : (
                            <div className="leading-relaxed text-sm">Uitgebreide feedback wordt geladen...</div>
                          )}
                      </div>
                    </div>

                    {evaluation.strongPoints && (
                      <div className="bg-green-50 p-5 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                          Je sterke punten
                        </h4>
                        <div className="text-green-700 prose prose-sm max-w-none break-words">
                          {formatContent(evaluation.strongPoints)}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    {evaluation.improvements && (
                      <div className="bg-yellow-50 p-5 rounded-lg">
                        <h4 className="font-semibold text-yellow-800 mb-3 flex items-center">
                          Aandachtspunten voor ontwikkeling
                        </h4>
                        <div className="text-yellow-700 prose prose-sm max-w-none break-words">
                          {formatContent(evaluation.improvements)}
                        </div>
                      </div>
                    )}

                    {evaluation.tips && (
                      <div className="bg-purple-50 p-5 rounded-lg">
                        <h4 className="font-semibold text-purple-800 mb-3 flex items-center">
                          Praktische tips voor volgend gesprek
                        </h4>
                        <div className="text-purple-700 prose prose-sm max-w-none break-words">
                          {formatContent(evaluation.tips)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="border-t pt-6">
                  <div className="text-center space-y-4">
                    <Button 
                      onClick={handleEvaluationContinue} 
                      size="lg"
                      className="px-8"
                    >
                      {evaluation.passed ? (
                        currentConversation < totalConversations ? 
                          `Ga naar Gesprek ${currentConversation + 1}` : 
                          'Module Voltooien'
                      ) : (
                        'Ga verder met oefenen'
                      )}
                    </Button>
                    
                    {!evaluation.passed && (
                      <div>
                        <Button 
                          onClick={handleStartOver} 
                          variant="outline" 
                          size="sm"
                        >
                          Of start volledig opnieuw
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card className="text-center p-8">
        <div className="w-8 h-8 border-3 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600">Gesprek wordt geladen...</p>
      </Card>
    </div>
  );
};

export default ConversationSimulation;
